import type { ServiceLabels } from '@/lib/form-state/util'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import {
    AgendaEvent,
    getCompanyAgendaEvents,
    getMyAgendaEvents,
} from '@/lib/api/agenda/agenda-api'
import {
    getSystemModules,
    SystemModule,
} from '@/lib/api/system-modules/system-modules-api'
import { usePermission } from '@/lib/hooks/use-permission'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useState } from 'react'
import {
    MdArrowForward,
    MdCalendarToday,
    MdEventAvailable,
    MdWavingHand,
} from 'react-icons/md'
import { Link } from 'react-router-dom'
import {
    isResourceHiddenForSegment,
    moduleIcon,
    moduleLabel,
} from '@/layouts/admin/segment-labels'
import {
    HIDE_RESOURCE_BY_SECONDARY_SEGMENT,
    SIDEBAR_MODULE_CONFIG,
} from '@/layouts/admin/sidebar-config'

function buildEventTypeLabels(labels: ServiceLabels): Record<string, string> {
    return {
        CONSULTATION: labels.Consultation,
        RETURN: 'Retorno',
        EXAM: labels.Exam,
        PROCEDURE: labels.Order,
        TELECONSULTATION: labels.Teleconsultation,
        OTHER: 'Compromisso',
    }
}

const EVENT_STATUS_BADGE: Record<string, { label: string; className: string }> =
    {
        PENDING: { label: 'Pendente', className: 'badge-warning' },
        CONFIRMED: { label: 'Confirmado', className: 'badge-success' },
        COMPLETED: { label: 'Concluído', className: 'badge-neutral' },
        NO_SHOW: { label: 'Faltou', className: 'badge-error' },
        OVERDUE: { label: 'Atrasado', className: 'badge-error' },
        NO_SHOW_JUSTIFIED: {
            label: 'Falta justificada',
            className: 'badge-ghost',
        },
        RESCHEDULED: { label: 'Remarcado', className: 'badge-info' },
    }

// Evento que se sobrepoe ao dia de hoje (inicio <= fim de hoje e fim >= inicio).
const overlapsToday = (ev: AgendaEvent, start: Date, end: Date): boolean => {
    const s = new Date(ev.startDate)
    const e = ev.endDate ? new Date(ev.endDate) : s
    return s < end && e >= start
}

const formatTime = (iso: string): string =>
    new Date(iso).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    })

interface WelcomeModule {
    key: string
    label: string
    icon: any
    href: string
    resourceCount: number
}

const greeting = (): string => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Bom dia'
    if (hour < 18) return 'Boa tarde'
    return 'Boa noite'
}

export default function WelcomePage() {
    const { user } = useAuth()
    const eventTypeLabels = buildEventTypeLabels(useServiceLabel())
    const { canAccessModule, canRead } = usePermission()
    const segment = user?.company?.segment || 'OTHER'
    const secondarySegment = user?.company?.secondarySegment || 'OTHER'

    const [systemModules, setSystemModules] = useState<SystemModule[]>([])
    const [loading, setLoading] = useState(true)

    const [todayEvents, setTodayEvents] = useState<AgendaEvent[]>([])
    const [loadingAgenda, setLoadingAgenda] = useState(true)

    useEffect(() => {
        let cancelled = false
        getSystemModules()
            .then(res => {
                if (!cancelled && res.success && res.result) {
                    setSystemModules(res.result)
                }
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [])

    // Eventos de HOJE: agenda pessoal (todo usuario tem) + agenda da empresa
    // (somente se o usuario tem acesso ao modulo de agenda). Filtra no cliente.
    useEffect(() => {
        let cancelled = false
        const canSeeCompany = canRead('atendimento', 'agenda')

        const load = async () => {
            const start = new Date()
            start.setHours(0, 0, 0, 0)
            const end = new Date(start)
            end.setDate(end.getDate() + 1)

            // O recorte do dia vai ao SERVIDOR, não só ao `overlapsToday` abaixo:
            // sem ele a home baixava todos os eventos de todos os anos para exibir
            // os de hoje.
            const range = {
                startDate: start.toISOString(),
                endDate: end.toISOString(),
            }

            const sources = [
                getMyAgendaEvents(range).catch(() => null),
                canSeeCompany
                    ? getCompanyAgendaEvents(range).catch(() => null)
                    : Promise.resolve(null),
            ]
            const responses = await Promise.all(sources)

            const byId = new Map<number, AgendaEvent>()
            for (const res of responses) {
                if (!res?.success || !Array.isArray(res.result)) continue
                for (const ev of res.result) {
                    if (ev.status === 'CANCELED') continue
                    if (!overlapsToday(ev, start, end)) continue
                    byId.set(ev.id, ev)
                }
            }

            const sorted = Array.from(byId.values()).sort(
                (a, b) =>
                    new Date(a.startDate).getTime() -
                    new Date(b.startDate).getTime()
            )

            if (!cancelled) {
                setTodayEvents(sorted)
                setLoadingAgenda(false)
            }
        }

        load()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Constroi os cards apenas dos modulos/recursos que o usuario pode acessar,
    // reaproveitando a mesma logica de visibilidade do sidebar (plano + role).
    const modules = useMemo<WelcomeModule[]>(() => {
        return systemModules
            .map(mod => {
                const config = SIDEBAR_MODULE_CONFIG[mod.key]
                if (!config) return null
                if (!canAccessModule(mod.key)) return null

                const visibleResources = mod.resources.filter(res => {
                    if (
                        HIDE_RESOURCE_BY_SECONDARY_SEGMENT[
                            secondarySegment
                        ]?.includes(res.key)
                    ) {
                        return false
                    }
                    if (isResourceHiddenForSegment(segment, res.key)) return false
                    if (!config.resources[res.key]) return false
                    return canRead(mod.key, res.key)
                })

                if (visibleResources.length === 0) return null

                const firstResource = visibleResources[0]
                return {
                    key: mod.key,
                    label: moduleLabel(segment, mod.key, mod.label),
                    icon: moduleIcon(segment, mod.key, config.icon),
                    href: config.resources[firstResource.key].href,
                    resourceCount: visibleResources.length,
                } as WelcomeModule
            })
            .filter(Boolean) as WelcomeModule[]
    }, [systemModules, segment, secondarySegment, canAccessModule, canRead])

    const firstName = user?.name?.split(' ')[0] || ''
    const companyName = user?.company?.tradingName || user?.company?.name || ''

    return (
        <div className="flex flex-col gap-8 max-w-5xl mx-auto w-full">
            <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg flex-shrink-0">
                    <MdWavingHand className="text-3xl text-primary-content" />
                </div>
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-base-content">
                        {greeting()}
                        {firstName ? `, ${firstName}` : ''}!
                    </h1>
                    <p className="text-base-content/60 text-sm">
                        {companyName
                            ? `Bem-vindo(a) a ${companyName}.`
                            : 'Bem-vindo(a) ao seu painel.'}
                    </p>
                </div>
            </div>

            <section>
                <h2 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <MdCalendarToday className="text-base" />
                    Agenda de hoje
                </h2>
                {loadingAgenda ? (
                    <div className="flex flex-col gap-2">
                        {Array.from({ length: 2 }).map((_, i) => (
                            <div
                                key={i}
                                className="h-16 rounded-xl bg-base-200/50 animate-pulse"
                            />
                        ))}
                    </div>
                ) : todayEvents.length === 0 ? (
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-base-100 border border-base-300/50">
                        <MdEventAvailable className="text-2xl text-success flex-shrink-0" />
                        <div>
                            <p className="text-base-content font-medium">
                                Nenhum compromisso para hoje
                            </p>
                            <p className="text-sm text-base-content/50">
                                Sua agenda está livre.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {todayEvents.map(ev => {
                            const badge = EVENT_STATUS_BADGE[ev.status]
                            const subtitleParts = [
                                eventTypeLabels[ev.type || 'OTHER'] ||
                                    'Compromisso',
                                ev.client?.name,
                                ev.agenda?.professional?.name,
                            ].filter(Boolean)
                            return (
                                <Link
                                    key={ev.id}
                                    to="/atendimento/agenda"
                                    className="group flex items-center gap-4 p-3 rounded-xl bg-base-100 border border-base-300/50 hover:border-secondary/50 hover:shadow-md transition-all"
                                >
                                    <div
                                        className="flex flex-col items-center justify-center px-3 py-1.5 rounded-lg bg-secondary/10 text-secondary min-w-[64px]"
                                        style={
                                            ev.color
                                                ? {
                                                      backgroundColor: `${ev.color}1a`,
                                                      color: ev.color,
                                                  }
                                                : undefined
                                        }
                                    >
                                        <span className="text-sm font-bold leading-tight">
                                            {formatTime(ev.startDate)}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-base-content truncate">
                                            {ev.title}
                                        </p>
                                        <p className="text-xs text-base-content/50 truncate">
                                            {subtitleParts.join(' · ')}
                                        </p>
                                    </div>
                                    {badge && (
                                        <span
                                            className={`badge badge-sm ${badge.className}`}
                                        >
                                            {badge.label}
                                        </span>
                                    )}
                                </Link>
                            )
                        })}
                    </div>
                )}
            </section>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div
                            key={i}
                            className="h-24 rounded-xl bg-base-200/50 animate-pulse"
                        />
                    ))}
                </div>
            ) : modules.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-10 text-center">
                    <p className="text-base-content/70">
                        Você ainda não possui módulos liberados.
                    </p>
                    <p className="text-base-content/50 text-sm mt-1">
                        Fale com o administrador da sua organização para receber
                        acesso.
                    </p>
                </div>
            ) : (
                <div>
                    <h2 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-3">
                        Acesso rápido
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {modules.map(mod => {
                            const Icon = mod.icon
                            return (
                                <Link
                                    key={mod.key}
                                    to={mod.href}
                                    className="group flex items-center gap-4 p-4 rounded-xl bg-base-100 border border-base-300/50 hover:border-secondary/50 hover:shadow-md transition-all"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-secondary/10 group-hover:bg-secondary/20 flex items-center justify-center flex-shrink-0 transition-colors">
                                        <Icon className="text-2xl text-secondary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-base-content truncate">
                                            {mod.label}
                                        </p>
                                        <p className="text-xs text-base-content/50">
                                            {mod.resourceCount}{' '}
                                            {mod.resourceCount === 1
                                                ? 'área disponível'
                                                : 'áreas disponíveis'}
                                        </p>
                                    </div>
                                    <MdArrowForward className="text-xl text-base-content/30 group-hover:text-secondary group-hover:translate-x-1 transition-all flex-shrink-0" />
                                </Link>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}
